import React from 'react'
import { connect } from 'react-redux'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import BackButton from '@pages/properties-view/apartments/v2/units-side-panel/back-button'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { CloseSvg, FullScreenSvg, Pin3Svg, Unpin3Svg } from '@svg/react'

interface ComponentProps {
  projectIdentity: ProjectIdentity
  fullScreenToggle: boolean
  setFullScreenToggle: (arg: boolean) => void
  isVideoPlaying: boolean
  isPinned: boolean
  hidePropertyControls?: boolean
  pinnedGalleryItemIndex: number | null
  currentSlideIndex: number
  onBackActionOverride?: () => void
  forceShowBackAction?: boolean
}

function Controls({
  projectIdentity,
  fullScreenToggle,
  setFullScreenToggle,
  isVideoPlaying,
  isPinned,
  hidePropertyControls,
  pinnedGalleryItemIndex,
  currentSlideIndex,
  onBackActionOverride,
  forceShowBackAction,
}: ComponentProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })
  const [isPinning, setIsPinning] = React.useState(false)

  const handleTogglePin = React.useCallback(async () => {
    if (isPinning) {
      return
    }
    const newPinnedIndex =
      pinnedGalleryItemIndex === null ? currentSlideIndex : null

    setIsPinning(true)
    try {
      await firebaseControlQuery.update({
        [`floorGallery.galleryControlV2.pinnedItemIndex`]: newPinnedIndex,
      })
    } catch {
      console.error('Failed to update pinned item index')
    } finally {
      setIsPinning(false)
    }
  }, [isPinning, pinnedGalleryItemIndex, currentSlideIndex])

  const handleContainerClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
    },
    []
  )

  return (
    <div
      className={`flex flex-col gap-[14px] text-white ${isVideoPlaying && 'hidden'}`}
      onClick={handleContainerClick}
      role="none"
    >
      {(forceShowBackAction || fullScreenToggle) && (
        <BackButton
          className="rounded bg-[#747474] p-1.5"
          iconClasses="h-5 w-5 stroke-white !scale-[1] !rotate-180"
          onOverrideClick={onBackActionOverride}
        />
      )}

      <button
        type="button"
        onClick={() => setFullScreenToggle(!fullScreenToggle)}
        className="rounded bg-[#747474] p-1.5"
      >
        {fullScreenToggle ? (
          <CloseSvg className="h-5 w-5 !scale-[.8] text-white" />
        ) : (
          <FullScreenSvg className="h-5 w-5 text-white" />
        )}
      </button>
      {hidePropertyControls ? null : (
        <button
          type="button"
          className="rounded bg-[#747474] p-1.5"
          onClick={handleTogglePin}
          disabled={isPinning}
        >
          {isPinned ? (
            <Unpin3Svg className="h-5 w-5" />
          ) : (
            <Pin3Svg className="h-5 w-5" />
          )}
        </button>
      )}
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    pinnedGalleryItemIndex:
      session?.floorGallery?.galleryControlV2?.pinnedItemIndex ?? null,
    projectIdentity,
  })
)(Controls)
